import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMurajaahQueue } from '@/features/murajaah/hooks';
import { QUEUE_TIER_LABELS, queueTierBadgeVariant } from '@/features/murajaah/labels';
import { useHasCapability } from '@/lib/capabilities';

const PREVIEW_LIMIT = 5;

/**
 * Murajaah Queue summary (Phase G-3, Teacher Workspace). Reuses the
 * existing `useMurajaahQueue` hook and the real `total` it returns - this
 * is a bounded preview of the same computed read-model the full Queue
 * page (`/murajaah/queue`, built in G-2) already renders, not a
 * duplicated queue implementation. Gated on `murajaah_sessions:manage`
 * exactly like the Queue page itself (ADR-022 Addendum, Decision 7.5) -
 * nav visibility, this card's own render, and the query's `enabled` flag
 * all key off the same capability, so a non-manage caller triggers zero
 * queue requests.
 */
export function MurajaahQueueSummaryCard(): JSX.Element | null {
  const canManage = useHasCapability('murajaah_sessions:manage');
  const { data, isLoading, isError } = useMurajaahQueue(1, PREVIEW_LIMIT, canManage);

  if (!canManage) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Antrian Murajaah</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat antrian murajaah.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Antrian murajaah kosong.</p>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
          <ul className="flex flex-col gap-2">
            {data.items.map((row) => (
              <li
                key={row.targetId}
                className="flex flex-row items-center justify-between gap-2 rounded-md border border-border p-2"
              >
                <span className="text-sm">
                  Surah {row.surah}: Ayat {row.startAyah}
                  {row.endAyah !== row.startAyah ? `-${row.endAyah}` : ''}
                </span>
                <Badge variant={queueTierBadgeVariant(row.tier)} className="w-fit shrink-0">
                  {QUEUE_TIER_LABELS[row.tier]}
                </Badge>
              </li>
            ))}
          </ul>
        )}

        {!isLoading && !isError && data !== undefined && (
          <Link to="/murajaah/queue" className="text-sm font-medium text-primary hover:underline">
            Lihat semua ({data.total})
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
